(() => {
  if (window.__JF_FIRST_OPTION_VARIANT_FIX__) return;
  window.__JF_FIRST_OPTION_VARIANT_FIX__ = true;

  const productPromises = new Map();
  const productData = new Map();
  const pickerObservers = new WeakMap();

  const escapeCss = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/"/g, '\\"');
  };

  const labelFor = (picker, input) => {
    if (!input || !input.id) return null;
    return picker.querySelector(`label[for="${escapeCss(input.id)}"]`);
  };

  const optionName = (picker, input) => {
    const explicit = input?.dataset?.jfOptionName;
    if (explicit) return explicit.trim();
    const label = labelFor(picker, input);
    if (!label) return '';
    const srOnly = label.querySelector('.sr-only');
    return (srOnly?.textContent || label.textContent || '').trim();
  };

  const handleFor = (picker) => picker?.getAttribute('handle') || '';

  const getProduct = (picker) => {
    const handle = handleFor(picker);
    if (!handle) return Promise.resolve(null);
    if (productData.has(handle)) return Promise.resolve(productData.get(handle));

    if (!productPromises.has(handle)) {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}products/${encodeURIComponent(handle)}.js`;
      const promise = fetch(url, { credentials: 'same-origin', cache: 'no-store' })
        .then((response) => {
          if (!response.ok) throw new Error(`Product JSON ${response.status}`);
          return response.json();
        })
        .then((product) => {
          productData.set(handle, product);
          return product;
        })
        .catch((error) => {
          productPromises.delete(handle);
          console.warn('[Justfishing] Variant availability fallback could not load product data.', error);
          return null;
        });

      productPromises.set(handle, promise);
    }

    return productPromises.get(handle);
  };

  const variantsForRoot = (product, rootName) => {
    if (!product?.variants || !rootName) return [];
    return product.variants.filter((variant) => {
      const options = variant.options || [variant.option1, variant.option2, variant.option3];
      return variant.available === true && options[0] === rootName;
    });
  };

  const setEnabledVisual = (picker, input) => {
    if (!input) return;
    if (input.disabled) input.disabled = false;
    input.removeAttribute('aria-disabled');

    const label = labelFor(picker, input);
    if (!label) return;
    if (label.classList.contains('is-disabled')) label.classList.remove('is-disabled');
    label.removeAttribute('aria-disabled');
    label.removeAttribute('disabled');
  };

  const syncRootAvailability = async (picker) => {
    const product = await getProduct(picker);
    if (!product || !picker.isConnected) return;

    const rootInputs = [...picker.querySelectorAll('input[data-option-position="1"]')];
    if (!rootInputs.length) return;

    rootInputs.forEach((input) => {
      const rootName = optionName(picker, input);
      const available = variantsForRoot(product, rootName).length > 0;
      input.dataset.jfRootAvailable = available ? 'true' : 'false';
      if (available) setEnabledVisual(picker, input);
    });

    picker.dataset.jfRootAvailabilityReady = 'true';
  };

  const currentSelections = (picker) => {
    const result = new Map();
    picker.querySelectorAll('input[data-option-position]:checked').forEach((input) => {
      const position = Number(input.dataset.optionPosition);
      if (position > 1) result.set(position, optionName(picker, input));
    });
    return result;
  };

  const compatibleVariant = (picker, product, rootInput) => {
    const rootName = optionName(picker, rootInput);
    const candidates = variantsForRoot(product, rootName);
    if (!candidates.length) return null;

    const selected = currentSelections(picker);
    const exact = candidates.find((variant) => {
      const options = variant.options || [variant.option1, variant.option2, variant.option3];
      for (const [position, value] of selected.entries()) {
        if (options[position - 1] !== value) return false;
      }
      return true;
    });

    return exact || candidates[0];
  };

  const preselectCompatibleChildren = (picker, variant) => {
    if (!variant) return;
    const options = variant.options || [variant.option1, variant.option2, variant.option3];

    options.forEach((wantedName, index) => {
      const position = index + 1;
      if (position === 1 || !wantedName) return;

      const inputs = [...picker.querySelectorAll(`input[data-option-position="${position}"]`)];
      const target = inputs.find((input) => optionName(picker, input) === wantedName);
      if (!target) return;

      setEnabledVisual(picker, target);
      target.checked = true;
    });
  };

  const prepareRootChoiceSync = (picker, rootInput) => {
    const product = productData.get(handleFor(picker));
    if (!product) return false;
    const variant = compatibleVariant(picker, product, rootInput);
    if (!variant) return false;
    setEnabledVisual(picker, rootInput);
    preselectCompatibleChildren(picker, variant);
    return true;
  };

  const rootInputFromEvent = (event) => {
    const directInput = event.target?.closest?.('input[data-option-position="1"]');
    if (directInput) return directInput;

    const label = event.target?.closest?.('label[for]');
    if (!label) return null;
    const picker = label.closest('variant-picker');
    if (!picker) return null;
    const input = picker.querySelector(`#${escapeCss(label.htmlFor)}`);
    return input?.matches?.('input[data-option-position="1"]') ? input : null;
  };

  document.addEventListener('pointerdown', (event) => {
    const rootInput = rootInputFromEvent(event);
    if (!rootInput) return;
    const picker = rootInput.closest('variant-picker');
    if (!picker || rootInput.dataset.jfRootAvailable !== 'true') return;
    setEnabledVisual(picker, rootInput);
    prepareRootChoiceSync(picker, rootInput);
  }, true);

  document.addEventListener('change', (event) => {
    const rootInput = event.target?.matches?.('input[data-option-position="1"]') ? event.target : null;
    if (!rootInput) return;
    const picker = rootInput.closest('variant-picker');
    if (!picker || rootInput.dataset.jfRootAvailable !== 'true') return;

    // Capture phase: make the later choices compatible before the theme's own
    // variant handler sees the event. This is what fixes e.g. 23g-only colours.
    prepareRootChoiceSync(picker, rootInput);
  }, true);

  const observePicker = (picker) => {
    if (pickerObservers.has(picker)) return;
    void syncRootAvailability(picker);

    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      queueMicrotask(() => {
        queued = false;
        if (!picker.isConnected) return;
        void syncRootAvailability(picker);
      });
    });

    observer.observe(picker, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled', 'class', 'aria-disabled']
    });

    pickerObservers.set(picker, observer);
  };

  const init = (root = document) => {
    if (root.matches?.('variant-picker')) observePicker(root);
    root.querySelectorAll?.('variant-picker').forEach(observePicker);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) init(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
